import { useContext, useEffect } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import OptionFormatter from "../../../../components/OptionFormatter";
import { useColorContext } from "../../../../context/ColorContext";
import {
  DebounceContext,
  DebounceContextType,
} from "../../../../context/DebounceContext";
import { useDecorationTechContext } from "../../../../context/DecorationTechContext";
import { useMaterialContext } from "../../../../context/MaterialContext";
import { useMoldContext } from "../../../../context/MoldContext";
import { useNonMoldContext } from "../../../../context/NonMoldContext";
import {
  componentTypeOptions,
  filterNonDigits,
  formatComponentTypeIntoOption,
} from "../../../../helpers";
import { getColors } from "../../../../helpers/color.helper";
import { getMaterials, toComponentMaterial } from "../../../../helpers/material.helper";
import { getMolds, getNonMolds } from "../../../../helpers/mold.helper";
import useDesign, { fetchDesign } from "../../../../hooks/useDesign";
import {
  ComponentType,
  ICompositionMpnComponent,
  IMpnComposition,
} from "../../../../types/data.interface";
import useAxios from "../../../../utils/useAxios";
import {
  checkComponentIsValid,
  formatIntoOption,
} from "../../features/mpn-create/mpn-create.helper";
import { useCreateMpnStore } from "../../context/create/CreateMpnContext";
import { CreateMpnActionTypes } from "../../states/create/data";
import { CreateMpnActions } from "../../states/create";
import { Predicates } from "../../../../libraries/predicates/predicates";
import { fetchNonMold } from "../../../../hooks/useNonMold";
import { fetchMold } from "../../../../hooks/useMold";
import { fetchMaterial } from "../../../../hooks/useMaterial";
import { fetchColor } from "../../../../hooks/useColor";
import { getDesigns } from "../../../../helpers/design.helper";
import { getDecorTechs } from "../../../../helpers/decor-tech.helper";
import { generateNodeId } from "../../../skus/features/sku-create/sku-create.helper";
import { formatCodeLabel, formatMoldLabel } from "../../helpers/resolveMoldedMoldId";

const AddEditSkuCompositionComponentForm = ({
  component,
  setComponent,
}: {
  component: ICompositionMpnComponent | null;
  setComponent: any;
}) => {
  const axios = useAxios();
  const { searchDebounce } = useContext<DebounceContextType>(DebounceContext);

  const { data: molds, isLoading: isMoldsLoading } = useMoldContext();
  const { data: nonMolds, isLoading: isNonMoldsLoading } = useNonMoldContext();
  const { data: materials, isLoading: isMaterialsLoading } =
    useMaterialContext();
  const { data: colors, isLoading: isColorsLoading } = useColorContext();
  const { data: decorationTechs, isLoading: isDecoTechsLoading } =
    useDecorationTechContext();
  const { data: artworks, isLoading: isArtworksLoading } = useDesign({
    decorationTechniqueId: String(component?.decoration_technique_id ?? ""),
  });

  const { state, dispatch } = useCreateMpnStore();

  const handleComponentType = (e: any) => {
    setComponent({
      ...component,
      type: e.value,
      mold_id: null,
      mold_description: null,
      is_tps: null,
      color_id: null,
      color_description: null,
      material_id: null,
      material_description: null,
      decoration_technique_id: null,
      decoration_technique_description: null,
      artwork_id: null,
      artwork_description: null,
      nr_pieces: null,
      mold_index: null,
      sequence_number:null,
    });
  };

  const handleMoldChange = (e: any) => {
    if (component?.type === "NonMolded") {
      setComponent({
        ...component,
        mold_id: e?.value.id ?? null,
        mold_description: e?.value.name ?? null,
        color_id: e.value.color.id,
        color_description: e.value.color.name,
        color_family_name: e.value.color.color_family_name,
        ...toComponentMaterial(e.value.material),
        decoration_technique_id: e.value.deco_tech.id,
        decoration_technique_description: e.value.deco_tech.name,
        artwork_id: e.value.artwork?.id && e.value.artwork.id,
        artwork_description: e.value.artwork?.id && e.value.artwork.description,
        sequence_number: e?.value.sequence_number ?? null,
      });
      return;
    }

    setComponent({
      ...component,
      mold_id: e?.value.id ?? null,
      mold_description: e?.value.name ?? null,
    });
  };

  const loadMoldOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3) return [];
    const response = await fetchMold({
      search,
      axios,
    });

    // callback(getMolds(possiblyRestrictedMolds(response, true)));
    callback(getMolds(response));
  };

  // const possiblyRestrictedMolds = (
  //   molds: IMold[],
  //   isSearch: boolean = false,
  // ) => {
  //   const tccCompositionMolds: IMold[] = tccComposition
  //     .map((composition) => composition.mold)
  //     .filter(Predicates.isNotNullAndNotUndefined)
  //     .sort((mold1, mold2) => Number(mold1.id) - Number(mold2.id));
  //
  //   if (
  //     selectedTccCompositionRegion?.is_restrictive &&
  //     tccCompositionMolds.length > 0
  //   ) {
  //     if (!isSearch) {
  //       return getUniqueItemsByProperties(tccCompositionMolds, ["id"]);
  //     } else {
  //       const moldIds: string[] = tccCompositionMolds.map((mold) => mold.id);
  //       return molds.filter((mold) => moldIds.includes(mold.id));
  //     }
  //   } else {
  //     return molds;
  //   }
  // };

  const loadNonMoldOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3) return [];
    const response = await fetchNonMold({
      search,
      axios,
    });

    // callback(getMolds(possiblyRestrictedNonMolds(response, true)));
    callback(getMolds(response));
  };

  // const possiblyRestrictedNonMolds = (
  //   nonMolds: IMold[],
  //   isSearch: boolean = false,
  // ) => {
  //   const tccCompositionNonMolds: IMold[] = tccComposition
  //     .map((composition) => composition.nonMold)
  //     .filter(Predicates.isNotNullAndNotUndefined)
  //     .sort((nonMold1, nonMold2) => Number(nonMold1.id) - Number(nonMold2.id));
  //
  //   if (
  //     // selectedTccCompositionRegion?.is_restrictive &&
  //     tccCompositionNonMolds.length > 0
  //   ) {
  //     if (!isSearch) {
  //       return getUniqueItemsByProperties(tccCompositionNonMolds, ["id"]);
  //     } else {
  //       const nonMoldIds: string[] = tccCompositionNonMolds.map(
  //         (nonMold) => nonMold.id,
  //       );
  //       return nonMolds.filter((nonMold) => nonMoldIds.includes(nonMold.id));
  //     }
  //   } else {
  //     return nonMolds;
  //   }
  // };

  const handleMaterialChange = (e: any) => {
    setComponent({
      ...component,
      ...toComponentMaterial(e?.value),
    });
  };

  const loadMaterialOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3) return [];
    const response = await fetchMaterial({
      search,
      axios,
    });

    callback(getMaterials(response));
  };

  const handleColorChange = (e: any) => {
    if (e?.value) {
      setComponent({
        ...component,
        color_id: e?.value.id ?? null,
        color_description: e?.value.name ?? null,
        color_family_name: e.value?.color_family_name,
        // color_family_id: e.value.color_family_id,
      });
      return
    }

    setComponent({
        ...component,
        color_id: null,
        color_description:  null,
        color_family_name: null,
      });
  };

  const loadColorOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3) return [];
    const response = await fetchColor({
      search,
      axios,
    });

    // callback(getColors(possiblyRestrictedColors(response, true)));
    callback(getColors(response));
  };

  // const possiblyRestrictedColors = (
  //   colors: IColor[],
  //   isSearch: boolean = false,
  // ) => {
  //   const tccCompositionColors: IColor[] = tccComposition
  //     .map((composition) => composition.color)
  //     .filter(Predicates.isNotNullAndNotUndefined)
  //     .sort((color1, color2) => Number(color1.id) - Number(color2.id));
  //
  //   if (
  //     selectedTccCompositionRegion?.is_restrictive &&
  //     tccCompositionColors.length > 0
  //   ) {
  //     if (!isSearch) {
  //       return getUniqueItemsByProperties(tccCompositionColors, ["id"]);
  //     } else {
  //       const colorIds: string[] = tccCompositionColors.map(
  //         (color) => color.id,
  //       );
  //       return colors.filter((color) => colorIds.includes(color.id));
  //     }
  //   } else {
  //     return colors;
  //   }
  // };

  const handleDecorationTechniqueChange = (e: any) => {
    setComponent({
      ...component,
      decoration_technique_id: e?.value.id ?? null,
      decoration_technique_description: e?.value.name ?? null,
      artwork_id: null,
      artwork_description: null,
    });
  };

  // const possiblyRestrictedDecorTechs = (decorTechs: IDecorationTech[]) => {
  //   const tccCompositionDecor: IDecorationTech[] = tccComposition
  //     .map((composition) => composition.decorTech)
  //     .filter(Predicates.isNotNullAndNotUndefined)
  //     .sort((deco1, deco2) => Number(deco1.id) - Number(deco2.id));
  //
  //   if (
  //     selectedTccCompositionRegion?.is_restrictive &&
  //     tccCompositionDecor.length > 0
  //   ) {
  //     return getUniqueItemsByProperties(tccCompositionDecor, ["id"]);
  //   } else {
  //     return decorTechs;
  //   }
  // };

  const handleArtworkChange = (e: any) => {
    setComponent({
      ...component,
      artwork_id: e?.value.id ?? null,
      artwork_description: e?.value.name ?? null,
    });
  };

  const loadDesignOptions = async (search: string, callback: any) => {
    if (
      !component?.decoration_technique_id &&
      (Predicates.isNullOrUndefined(search) || search.length < 3)
    )
      return [];
    const response = await fetchDesign({
      decorationTechniqueId: String(component?.decoration_technique_id),
      search,
      axios,
    });

    // callback(getDesigns(possiblyRestrictedDesigns(response, true)));
    callback(getDesigns(response));
  };

  // const possiblyRestrictedDesigns = (
  //   designs: IDesign[],
  //   isSearch: boolean = false,
  // ) => {
  //   const tccCompositionArtworks: IDesign[] = tccComposition
  //     .map((composition) => composition.artwork)
  //     .filter(Predicates.isNotNullAndNotUndefined)
  //     .filter(
  //       (artwork) =>
  //         artwork.deco_tech_id ===
  //         String(component?.decoration_technique_id ?? -1),
  //     )
  //     .sort((artwork1, artwork2) => Number(artwork1.id) - Number(artwork2.id));
  //
  //   if (
  //     selectedTccCompositionRegion?.is_restrictive &&
  //     tccCompositionArtworks.length > 0
  //   ) {
  //     if (!isSearch) {
  //       return getUniqueItemsByProperties(tccCompositionArtworks, [
  //         "unique_id",
  //       ]);
  //     } else {
  //       const arworkIds: number[] = tccCompositionArtworks.map(
  //         (artwork) => artwork.unique_id,
  //       );
  //       return designs.filter((artwork) =>
  //         arworkIds.includes(artwork.unique_id),
  //       );
  //     }
  //   } else {
  //     return designs;
  //   }
  // };

  const handlePiecesChange = (e: any) => {
    setComponent({
      ...component,
      nr_pieces:
        filterNonDigits(e.target.value).length > 0
          ? parseInt(filterNonDigits(e.target.value))
          : undefined,
    });
  };

  const handleIndexChange = (e: any) => {
    setComponent({
      ...component,
      mold_index:
        filterNonDigits(e.target.value).length > 0
          ? parseInt(filterNonDigits(e.target.value))
          : undefined,
    });
  };

  const handleTpsChange = (e: any) => {
    setComponent({ ...component, is_tps: e.target.checked });
  };

  const moldIndexIsUnique = (isNewComponent: boolean) => {
    return !isNewComponent && component
      ? !state.composition.non_molded_components.some(
          (nonMold) =>
            nonMold.node_id !== component.node_id &&
            nonMold.mold_index === component.mold_index,
        ) &&
          !state.composition.molded_components.some(
            (mold) =>
              mold.node_id !== component.node_id &&
              mold.mold_index === component.mold_index,
          )
      : !state.composition.molded_components
          .map((mold) => mold.mold_index)
          .includes(component?.mold_index) &&
          !state.composition.non_molded_components
            .map((nonMold) => nonMold.mold_index)
            .includes(component?.mold_index);
  };

  const saveComponent = (isNew: boolean) => {
    const moldedNonMoldedKey: keyof IMpnComposition =
      component?.type === "Molded"
        ? "molded_components"
        : "non_molded_components";
    const updatedComposition = state.composition[moldedNonMoldedKey];

    if (isNew) {
      if (component) {
        component["node_id"] = generateNodeId()
        component.quantity = 1;
      }

      // @ts-ignore
      updatedComposition.push(component);
      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_COMPOSITION, {
          ...state.composition,
          [moldedNonMoldedKey]: updatedComposition,
        }),
      );
      return;
    }

    const currentIndex = state.composition[moldedNonMoldedKey].findIndex(
      (c) =>
        c.node_id === component?.node_id
    );
    if (currentIndex >= 0) {
      //@ts-ignore
      updatedComposition[currentIndex] = { ...component };

      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_COMPOSITION, {
          ...state.composition,
          [moldedNonMoldedKey]: updatedComposition,
        }),
      );
    }

    // if (
    //   Predicates.isNotNullAndNotUndefined(component?.mold_index) &&
    //   !moldIndexIsUnique(isNew)
    // ) {
    //   toast.error(`Mold Index ${component?.mold_index} is already in use.`);
    //   return;
    // }
    // if (
    //   component &&
    //   (checkIfNewComponentAlreadyExists(
    //     component,
    //     savedSkuComposition.molded_components,
    //     isNew,
    //   ) ||
    //     checkIfNewComponentAlreadyExists(
    //       component,
    //       savedSkuComposition.non_molded_components,
    //       isNew,
    //     ))
    // ) {
    //   toast.error(
    //     `You're trying to add a ${component.type === "NonMolded" ? "Non-Molded" : "Molded"} component that already exists. Please change something on the new component or alter the quantity of the existing one`,
    //   );
    //   return;
    // }
    // const nodeId = component?.node_id && !isNew ? component.node_id : -1;
    // if (component?.type === "NonMolded") {
    //   handleAddComponents(
    //     [],
    //     [{ ...component, quantity: 1, type: "NonMolded", node_id: nodeId }],
    //     nodeId,
    //     isNew,
    //   );
    // } else if (component?.type === "Molded") {
    //   handleAddComponents(
    //     [{ ...component, quantity: 1, type: "Molded", node_id: nodeId }],
    //     [],
    //     nodeId,
    //     isNew,
    //   );
    // }
    // if (isNew) {
    //   setComponent({ ...component, node_id: -1 });
    // } else {
    cleanAllFields();
    // }
  };

  const cleanAllFields = () => {
    setComponent(null);
  };

  // @ts-ignore
  return (
    <div className="row mb-2">
      <div className="col-md-3 form-group">
        <label className="form-label">
          Type <span className="red-text fw-bold">*</span>
        </label>
        <Select
          options={componentTypeOptions}
          onChange={handleComponentType}
          value={formatComponentTypeIntoOption(component?.type ?? "")}
          placeholder=""
          formatOptionLabel={OptionFormatter}
          classNamePrefix="react-select"
          components={{
            IndicatorSeparator: () => null,
          }}
        />
      </div>

      {component?.type !== "NonMolded" &&
        <div className="col-md-3 form-group">
          <label className="form-label">
            Mold
            <span className="red-text fw-bold">*</span>
          </label>
          <AsyncSelect
            cacheOptions
            loadOptions={(input, callback) => {
              if (component?.type === "NonMolded") {
                searchDebounce(
                  loadNonMoldOptions,
                  input,
                  getMolds(nonMolds),
                  callback,
                );
              } else {
                searchDebounce(
                  loadMoldOptions,
                  input,
                  getMolds(molds),
                  callback,
                );
              }
            }}
            //@ts-ignore
            defaultOptions={getMolds(molds)}
            onChange={handleMoldChange}
            value={
              component?.mold_description
                ? {
                    label: formatMoldLabel(component?.mold_id, component?.mold_description),
                    value: component,
                  }
                : null
            }
            placeholder="(min 3 characters)"
            formatOptionLabel={OptionFormatter}
            isLoading={isMoldsLoading}
            classNamePrefix="react-select"
            isClearable
            components={{
              IndicatorSeparator: () => null,
            }}
            isDisabled={!component?.type}
          />
        </div>
      }

      {component?.type === "NonMolded" &&
        <div className="col-md-3 form-group">
          <label className="form-label">
            Sequence
            <span className="red-text fw-bold">*</span>
          </label>
          <AsyncSelect
            cacheOptions
            loadOptions={(input, callback) => {
                searchDebounce(
                  loadNonMoldOptions,
                  input,
                  getNonMolds(nonMolds),
                  callback,
                );
            }}
            //@ts-ignore
            defaultOptions={getNonMolds(nonMolds)}
            onChange={handleMoldChange}
            value={
              component?.mold_description
                ? {
                  label: `${component?.sequence_number} - ${component?.mold_description}`,
                  value: component,
                }
                : null
            }
            placeholder="(min 3 characters)"
            formatOptionLabel={OptionFormatter}
            isLoading={isMoldsLoading || isNonMoldsLoading}
            classNamePrefix="react-select"
            isClearable
            components={{
              IndicatorSeparator: () => null,
            }}
            isDisabled={!component?.type}
          />
        </div>
      }

      <div className="col-md-3 form-group">
        <label className="form-label">
          Material
          {component?.type !== "NonMolded" && (
            <span className="red-text fw-bold">*</span>
          )}
        </label>
        <AsyncSelect
          cacheOptions
          loadOptions={(input, callback) => {
            searchDebounce(
              loadMaterialOptions,
              input,
              getMaterials(materials),
              callback,
            );
          }}
          //@ts-ignore
          defaultOptions={getMaterials(materials)}
          onChange={handleMaterialChange}
          value={
            component?.type === "NonMolded"
              ? component?.material_id
                ? {
                    label: formatCodeLabel(component?.material_id, component?.material_description),
                    value: {
                      id: component?.material_id,
                      name: component?.material_description,
                      four_digit_code: "",
                    },
                  }
                : null
              : component?.material_id
                ? {
                    label: formatCodeLabel(component?.material_id, component?.material_description),
                    value: {
                      id: component?.material_id,
                      name: component?.material_description,
                      four_digit_code: "",
                    },
                  }
                : null
          }
          placeholder={
            component?.type && component.type === "NonMolded"
              ? ""
              : "(min 3 characters)"
          }
          formatOptionLabel={OptionFormatter}
          classNamePrefix="react-select"
          isClearable
          isLoading={isMaterialsLoading}
          components={{
            IndicatorSeparator: () => null,
          }}
          isDisabled={!component?.type}
        />
      </div>

      <div className="col-md-3 form-group">
        <label className="form-label">
          Color{" "}
          {component?.type !== "NonMolded" && (
            <span className="red-text fw-bold">*</span>
          )}
        </label>
        <AsyncSelect
          cacheOptions
          loadOptions={(input, callback) => {
            searchDebounce(
              loadColorOptions,
              input,
              getColors(colors),
              callback,
            );
          }}
          //@ts-ignore
          defaultOptions={getColors(colors)}
          onChange={handleColorChange}
          value={
            component?.color_id
              ? {
                  label: formatCodeLabel(component?.color_id, component?.color_description),
                  value: {
                    itn_colcod: component?.color_id,
                    itn_colnam: component?.color_description,
                  },
                }
              : null
          }
          placeholder={
            component?.type && component.type === "NonMolded"
              ? ""
              : "(min 3 characters)"
          }
          formatOptionLabel={OptionFormatter}
          classNamePrefix="react-select"
          isClearable
          isLoading={isColorsLoading}
          components={{
            IndicatorSeparator: () => null,
          }}
          isDisabled={!component?.type}
        />
      </div>

      <div className="col-md-3 form-group">
        <label className="form-label">Decoration Technique</label>
        <Select
          //@ts-ignore
          options={getDecorTechs(decorationTechs)}
          onChange={handleDecorationTechniqueChange}
          value={
            component?.decoration_technique_id &&
            `${component.decoration_technique_id}` !== "0"
              ? {
                  label: formatCodeLabel(component.decoration_technique_id, component.decoration_technique_description),
                  value: {
                    id: component.decoration_technique_id,
                    name: component.decoration_technique_description,
                  },
                }
              : null
          }
          // value={
          //   Predicates.isNotNullAndNotUndefinedAndNotEmpty(component) && `${component?.decoration_technique_id}` != "0" && component?.decoration_technique_id != 0
          //     ? {
          //         label: `${component?.decoration_technique_id} - ${component?.decoration_technique_description}`,
          //         value: component?.decoration_technique_id,
          //       }
          //     : null
          // }
          placeholder=""
          formatOptionLabel={OptionFormatter}
          classNamePrefix="react-select"
          isLoading={isDecoTechsLoading}
          isClearable
          components={{
            IndicatorSeparator: () => null,
          }}
          isDisabled={!component?.type}
        />
      </div>

      <div className="col-md-3 form-group">
        <label className="form-label">Artwork</label>
        <AsyncSelect
          isDisabled={
            !component?.decoration_technique_id ||
            component?.decoration_technique_id <= 0 ||
            !component?.type
          }
          loadOptions={(input, callback) => {
            searchDebounce(
              loadDesignOptions,
              input,
              getDesigns(artworks),
              callback,
            );
          }}
          //@ts-ignore
          defaultOptions={getDesigns(artworks)}
          onChange={handleArtworkChange}
          value={
            component?.artwork_id &&
            `${component.artwork_id}` !== "0000"
              ? {
                  label: formatCodeLabel(String(component.artwork_id).padStart(4, "0"), component.artwork_description),
                  value: {
                    id: component.artwork_id,
                    name: component.artwork_description,
                  },
                }
              : null
          }
          placeholder="(min 3 characters)"
          formatOptionLabel={OptionFormatter}
          isLoading={isArtworksLoading}
          classNamePrefix="react-select"
          isClearable
          components={{
            IndicatorSeparator: () => null,
          }}
        />
      </div>

      <div className="col-1 form-group">
        <label className="form-label">
          Pcs/Sets <span className="red-text fw-bold">*</span>
        </label>
        <input
          value={component?.nr_pieces ?? ""}
          onChange={handlePiecesChange}
          className="form-control"
          type="text"
          disabled={!component?.type}
        />
      </div>

      <div className="col-1 form-group">
        <label className="form-label">Mold Index</label>
        <input
          value={component?.mold_index ?? ""}
          onChange={handleIndexChange}
          className="form-control"
          type="text"
          disabled={!component?.type}
        />
      </div>

      {component?.type !== "NonMolded" ? (
        <div className="col-md-1 form-group d-flex flex-column align-items-center">
          <label className="form-label">TPS</label>
          <div className="fixed-checkbox-height">
            <input
              onChange={handleTpsChange}
              name="discontinued"
              checked={component?.is_tps}
              type="checkbox"
              className="checkbox"
              disabled={!component?.type}
            />
          </div>
        </div>
      ) : (
        <div className="col-md-1"></div>
      )}
      <div className="col-md-3 form-group align-self-end text-end pr-1">
        <input
          type="submit"
          className="btn btn-primary me-3"
          onClick={() => {
            saveComponent(false);
            cleanAllFields();
          }}
          disabled={
            !checkComponentIsValid(component) ||
            Predicates.isNullOrUndefined(component?.node_id) ||
            (component?.node_id ?? 0) <= 0
          }
          value={"Save Edit"}
        />
        <input
          type="submit"
          className="btn btn-primary"
          onClick={() => {
            saveComponent(true);
            cleanAllFields();
          }}
          disabled={
            !Predicates.isNullOrUndefined(component?.node_id) ||
            !checkComponentIsValid(component)
          }
          value={"Add New"}
        />
      </div>
    </div>
  );
};

export default AddEditSkuCompositionComponentForm;
